import React, { useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import {
  IDENTITY_FAILED,
  IDENTITY_PENDING,
  IDENTITY_VERIFIED,
  getIdentityStatus,
  requiresIdentityVerification,
  createVerificationSession,
} from '../../utils/identityVerification';
import './Identity.css';

const DEFAULT_ERROR =
  'Something went wrong starting verification, please try again.';

/**
 * Renders only for paid tiers that still need to verify. Free tier, and any
 * already-verified user, render nothing. `status` can be forced by the caller
 * (the return-from-Stripe page uses it to offer a retry after a timeout).
 */
const VerifyIdentityButton = ({
  user,
  status: statusOverride,
  showPendingState = true,
  className = '',
  variant = 'primary',
}) => {
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState('');

  const status = statusOverride || getIdentityStatus(user);

  if (user && !requiresIdentityVerification(user)) return null;
  if (status === IDENTITY_VERIFIED) return null;

  if (status === IDENTITY_PENDING) {
    if (!showPendingState) return null;
    return (
      <div className={`identity-pending-note ${className}`.trim()} role="status">
        <Loader2 size={16} className="identity-spin" aria-hidden="true" />
        <span>Verification in progress — this can take a few minutes.</span>
      </div>
    );
  }

  const hasFailed = status === IDENTITY_FAILED;

  const handleClick = async () => {
    setIsStarting(true);
    setError('');
    try {
      const verificationUrl = await createVerificationSession();
      if (!verificationUrl) {
        throw new Error('No verification URL returned');
      }
      // Full-page handoff to Stripe; leave the button in its loading state.
      window.location.href = verificationUrl;
    } catch (err) {
      const message = err.response?.data?.message;
      setError(typeof message === 'string' && message ? message : DEFAULT_ERROR);
      setIsStarting(false);
    }
  };

  return (
    <div className={`identity-verify-action ${className}`.trim()}>
      <button
        type="button"
        className={`btn btn-${variant}`}
        onClick={handleClick}
        disabled={isStarting}
      >
        {isStarting ? (
          <>
            <Loader2 size={16} className="identity-spin" aria-hidden="true" />
            Starting verification...
          </>
        ) : (
          <>
            <ShieldCheck size={16} aria-hidden="true" />
            {hasFailed ? 'Try verification again' : 'Verify my identity'}
          </>
        )}
      </button>
      {error && (
        <p className="identity-verify-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default VerifyIdentityButton;
